import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { routes, PRODUCT_VALUE_RECOVERY_KIND, type ProductValueRecoveryPlan } from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

export function useProductValueRecovery(marketplace: string, originatingRunId: number) {
  const client = useQueryClient()
  const [confirmedToken, setConfirmedToken] = useState<string | null>(null)
  const plan = useQuery({
    queryKey: queryKeys.productValueRecoveryPlan(marketplace, originatingRunId),
    queryFn: async ({ signal }) => {
      const response = await apiFetch<ProductValueRecoveryPlan>(
        routes.productValues.recoveryPlan({ marketplace }),
        { signal, query: { originatingRunId: String(originatingRunId) } }
      )
      if (!response.ok) {
        throw new Error(response.error.message)
      }
      return response.value
    },
    staleTime: 10_000,
    refetchOnMount: "always",
    refetchInterval: (query) => (isRecoveryComplete(query.state.data) ? false : 5_000),
    retry: false,
  })
  const isComplete = isRecoveryComplete(plan.data)
  const canUsePlan = plan.isFetchedAfterMount && !plan.isFetching && !plan.isError && !plan.isStale
  const review = plan.data?.review
  const token = review?.activationInput.previewToken
  const isConfirmed = Boolean(token && confirmedToken === token)

  useEffect(() => {
    setConfirmedToken(null)
  }, [token, plan.isError])

  const refresh = async () => {
    await client.invalidateQueries({ queryKey: queryKeys.productValueRecoveryPlan(marketplace) })
    await client.invalidateQueries({ queryKey: ["run"] })
  }
  const build = useMutation({
    mutationFn: async () => {
      if (!canUsePlan || plan.data?.kind !== PRODUCT_VALUE_RECOVERY_KIND.buildRequired) {
        throw new Error("Load a fresh recovery plan before building.")
      }
      const response = await apiFetch(routes.productValues.generations(), {
        method: "POST",
        body: { marketplace },
      })
      if (!response.ok) {
        throw new Error(response.error.message)
      }
    },
    onSettled: refresh,
  })
  const activate = useMutation({
    mutationFn: async () => {
      if (
        !canUsePlan ||
        plan.data?.kind !== PRODUCT_VALUE_RECOVERY_KIND.reviewable ||
        !review ||
        (!review.rollback.available && !isConfirmed)
      ) {
        throw new Error("Load and confirm a fresh recovery plan before activation.")
      }
      const response = await apiFetch(routes.productValues.activate(), {
        method: "POST",
        body: { ...review.activationInput, requestId: crypto.randomUUID(), confirm: true },
      })
      if (!response.ok) {
        throw new Error(response.error.message)
      }
    },
    onSettled: async () => {
      setConfirmedToken(null)
      await refresh()
    },
  })

  useEffect(() => {
    if (isComplete) {
      void client.invalidateQueries({ queryKey: ["run"] })
    }
  }, [client, isComplete])

  return {
    plan,
    isComplete,
    build,
    activate,
    isConfirmed,
    canAct: canUsePlan && !build.isPending && !activate.isPending,
    confirm: (checked: boolean) => setConfirmedToken(checked && token ? token : null),
  }
}

function isRecoveryComplete(plan: ProductValueRecoveryPlan | undefined) {
  return Boolean(plan?.marketplaceCurrent && plan.kind === PRODUCT_VALUE_RECOVERY_KIND.replaying)
}
